import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

export interface UseMetricsResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useMetrics<T>(
  endpoint: string,
  refreshInterval?: number
): UseMetricsResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();

  const fetchMetrics = useCallback(async () => {
    const apiKey = token;
    if (!apiKey) {
      setError('API key not found');
      setLoading(false);
      return;
    }

    try {
      const apiUrl = process.env.API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid or expired API key');
        } else if (response.status === 403) {
          throw new Error('Access forbidden');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const jsonData = await response.json();
      setData(jsonData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [endpoint, token]);

  useEffect(() => {
    fetchMetrics();

    if (refreshInterval && refreshInterval > 0) {
      const intervalId = setInterval(fetchMetrics, refreshInterval);
      return () => clearInterval(intervalId);
    }
  }, [fetchMetrics, refreshInterval]);

  return {
    data,
    loading,
    error,
    refetch: fetchMetrics,
  };
}
